import { createPortal } from "react-dom";
import { useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Printer } from "lucide-react";

const CLUBS = [
  { name: "Diplomatas", emoji: "🤝" },
  { name: "Analistas", emoji: "🧠" },
  { name: "Sentinelas", emoji: "🛡️" },
  { name: "Exploradores", emoji: "🚀" },
];

const fmtDeadline = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
};

/* Cartaz A4 pronto a imprimir: QR, mote e prazo das candidaturas. */
export function ApplyPoster({ url, deadlineIso, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const deadline = fmtDeadline(deadlineIso);

  return createPortal((
    <div className="fixed inset-0 z-[110] bg-black/90 overflow-y-auto p-6 print:p-0 print:bg-white print:static print:overflow-visible"
      data-testid="apply-poster">
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          body > *:not(.kw-poster-root) { display: none !important; }
          .kw-poster-root { position: static !important; }
          .kw-poster-noprint { display: none !important; }
          .kw-poster-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>

      <div className="kw-poster-root">
        <div className="kw-poster-noprint max-w-[210mm] mx-auto flex items-center justify-end gap-2 mb-4">
          <button onClick={() => window.print()} className="kw-btn-primary inline-flex items-center gap-2"
            data-testid="apply-poster-print">
            <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
          </button>
          <button onClick={onClose} className="kw-btn-secondary inline-flex items-center gap-2"
            data-testid="apply-poster-close">
            <X className="w-4 h-4" /> Fechar
          </button>
        </div>

        <div className="kw-poster-sheet bg-[#0A0A0A] text-white mx-auto shadow-2xl"
          style={{ width: "210mm", minHeight: "297mm", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
          <div className="h-full flex flex-col px-16 py-14">
            <div className="flex items-center gap-3 mb-10">
              <span className="w-3 h-3 bg-[#E50000] rounded-full" />
              <span className="text-[11px] uppercase tracking-[0.4em] font-bold">Growth Clubs KW Lead</span>
            </div>

            <h1 className="font-display text-[62px] leading-[0.88] font-black uppercase tracking-tighter">
              Do Auto-<br />conhecimento<br />
              <span className="text-[#E50000]">ao Crescimento</span>
            </h1>

            <p className="text-lg font-bold mt-8">Este não é apenas um formulário de inscrição.</p>
            <p className="text-lg text-[#E50000] font-bold">É o primeiro passo de um compromisso.</p>

            <div className="flex items-stretch gap-10 mt-10">
              <div className="bg-white p-4 shrink-0">
                <QRCodeCanvas value={url} size={210} level="M" includeMargin={false} />
              </div>
              <div className="flex-1 flex flex-col justify-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#737373] mb-1">Aponta a câmara do telemóvel</div>
                  <div className="font-display text-2xl font-black uppercase tracking-tight">Candidata-te em 3 minutos</div>
                </div>
                <div className="font-mono text-sm text-[#E50000] break-all" data-testid="apply-poster-url">{url}</div>
                {deadline ? (
                  <div className="border border-[#EAB308] px-4 py-3" data-testid="apply-poster-deadline">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#EAB308] mb-1">Candidaturas até</div>
                    <div className="font-display text-xl font-black uppercase tracking-tight text-[#EAB308]">{deadline}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] mt-1">Fecha às 24:00</div>
                  </div>
                ) : (
                  <div className="border border-[#333] px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]"
                    data-testid="apply-poster-deadline">
                    Prazo anunciado em breve
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mt-10">
              {CLUBS.map((c) => (
                <div key={c.name} className="border border-[#262626] px-3 py-4 text-center">
                  <div className="text-3xl mb-2">{c.emoji}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold">{c.name}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-10 mt-8">
              <div>
                <div className="font-display text-4xl font-black text-[#E50000] leading-none">8</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-1">Concorrentes por clube</div>
              </div>
              <div>
                <div className="font-display text-4xl font-black leading-none">13</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-1">Semanas de percurso</div>
              </div>
              <div>
                <div className="font-display text-4xl font-black leading-none">3<span className="text-base">min</span></div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-1">A preencher</div>
              </div>
            </div>

            <div className="mt-auto pt-10 border-t border-[#262626]">
              <div className="font-display text-2xl font-black uppercase tracking-tight leading-tight">
                Coopetimos para crescer.<br />
                <span className="text-[#E50000]">Não para ganhar a qualquer custo.</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#555] mt-3">Regra de Ouro · KW Lead</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
